/**
 * Badge Service
 *
 * Handles CRUD operations for badges and user badge assignments.
 * Manages badge system including awarding, tracking, and display.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/firebase";
import {
  COLLECTIONS,
  BADGE_CATEGORIES,
  BADGE_AWARD_SOURCES,
  validateBadge,
  validateUserBadge,
} from "./database.schema";

/**
 * Badge Management Functions
 */

/**
 * Get all available badges
 * @param {Object} options - Query options
 * @param {boolean} options.activeOnly - Return only active badges
 * @param {string} options.category - Filter by category
 * @returns {Promise<Array>} Array of badge objects
 */
export async function getBadges(options = {}) {
  try {
    const { activeOnly = true, category = null } = options;

    // Use the simplest possible query to avoid index requirements
    const badgeQuery = collection(db, COLLECTIONS.BADGES);
    const snapshot = await getDocs(badgeQuery);
    let badges = [];

    snapshot.forEach((doc) => {
      badges.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    // If no badges exist, initialize defaults (for development)
    if (badges.length === 0) {
      console.log("No badges found, initializing default badges...");
      try {
        await initializeDefaultBadges();
        // Retry fetching badges after initialization
        const retrySnapshot = await getDocs(badgeQuery);
        badges = [];
        retrySnapshot.forEach((doc) => {
          badges.push({
            id: doc.id,
            ...doc.data(),
          });
        });
      } catch (initError) {
        if (initError.code === "permission-denied") {
          console.warn(
            "Badge initialization requires admin permissions. Badges will be unavailable until an admin initializes them."
          );
        } else {
          console.warn("Could not initialize default badges:", initError);
        }
        // Return empty array if we can't initialize - UI will handle gracefully
        return [];
      }
    }

    // Filter in JavaScript to avoid index requirements
    let filteredBadges = badges;

    if (activeOnly) {
      filteredBadges = filteredBadges.filter(
        (badge) => badge.isActive === true
      );
    }

    if (category && Object.values(BADGE_CATEGORIES).includes(category)) {
      filteredBadges = filteredBadges.filter(
        (badge) => badge.category === category
      );
    }

    // Sort by sortOrder and name
    filteredBadges.sort((a, b) => {
      const sortOrderA = a.sortOrder || 0;
      const sortOrderB = b.sortOrder || 0;
      if (sortOrderA !== sortOrderB) {
        return sortOrderA - sortOrderB;
      }
      return (a.name || "").localeCompare(b.name || "");
    });

    return filteredBadges;
  } catch (error) {
    console.error("Error fetching badges:", error);
    // If it's a permission error, return empty array to prevent breaking the UI
    if (error.code === "permission-denied") {
      console.warn("Badges not accessible, returning empty array");
      return [];
    }
    throw new Error("Failed to fetch badges");
  }
}

/**
 * Get a specific badge by ID
 * @param {string} badgeId - Badge ID
 * @returns {Promise<Object|null>} Badge object or null if not found
 */
export async function getBadgeById(badgeId) {
  try {
    const badgeDoc = await getDoc(doc(db, COLLECTIONS.BADGES, badgeId));

    if (!badgeDoc.exists()) {
      return null;
    }

    return {
      id: badgeDoc.id,
      ...badgeDoc.data(),
    };
  } catch (error) {
    console.error("Error fetching badge:", error);
    throw new Error("Failed to fetch badge");
  }
}

/**
 * Create a new badge
 * @param {Object} badgeData - Badge data
 * @returns {Promise<string>} Created badge ID
 */
export async function createBadge(badgeData) {
  try {
    // Validate badge data
    validateBadge(badgeData);

    const badge = {
      ...badgeData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      sortOrder: badgeData.sortOrder || 0,
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.BADGES), badge);

    console.log("Badge created successfully:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error creating badge:", error);
    throw new Error("Failed to create badge");
  }
}

/**
 * Update an existing badge
 * @param {string} badgeId - Badge ID
 * @param {Object} updates - Badge updates
 * @returns {Promise<void>}
 */
export async function updateBadge(badgeId, updates) {
  try {
    // If critical fields are being updated, we must fetch the existing
    // document to run a full validation on the merged data.
    if (updates.name || updates.description || updates.category) {
      const docSnap = await getDoc(doc(db, COLLECTIONS.BADGES, badgeId));
      if (!docSnap.exists()) {
        throw new Error("Badge not found");
      }

      // Get the data without the ID for validation
      const existingData = docSnap.data();
      const dataToValidate = { ...existingData, ...updates };

      // This call now correctly lacks the 'id' property.
      validateBadge(dataToValidate);
    }

    // Proceed with the update
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(doc(db, COLLECTIONS.BADGES, badgeId), updateData);

    console.log("Badge updated successfully:", badgeId);
  } catch (error) {
    console.error("Error updating badge:", error);
    if (error.message === "Badge not found") {
      throw error;
    }
    throw new Error("Failed to update badge");
  }
}

/**
 * Delete a badge (soft delete by setting isActive to false)
 * @param {string} badgeId - Badge ID
 * @returns {Promise<void>}
 */
export async function deleteBadge(badgeId) {
  try {
    await updateDoc(doc(db, COLLECTIONS.BADGES, badgeId), {
      isActive: false,
      updatedAt: serverTimestamp(),
    });

    console.log("Badge deleted successfully:", badgeId);
  } catch (error) {
    console.error("Error deleting badge:", error);
    throw new Error("Failed to delete badge");
  }
}

/**
 * User Badge Management Functions
 */

/**
 * Get badges for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of user badge objects with badge details
 */
export async function getUserBadges(userId) {
  try {
    const userBadgesQuery = query(
      collection(db, COLLECTIONS.USER_BADGES),
      where("userId", "==", userId),
      orderBy("awardedAt", "desc")
    );

    const snapshot = await getDocs(userBadgesQuery);
    const userBadges = [];

    // Get badge details for each user badge
    const badgePromises = [];
    snapshot.forEach((doc) => {
      const userBadgeData = { id: doc.id, ...doc.data() };
      userBadges.push(userBadgeData);
      badgePromises.push(getBadgeById(userBadgeData.badgeId));
    });

    const badgeDetails = await Promise.all(badgePromises);

    // Combine user badge data with badge details
    return userBadges
      .map((userBadge, index) => ({
        ...userBadge,
        badge: badgeDetails[index],
      }))
      .filter((item) => item.badge !== null); // Filter out badges that don't exist
  } catch (error) {
    console.error("Error fetching user badges:", error);
    throw new Error("Failed to fetch user badges");
  }
}

/**
 * Award a badge to a user
 * @param {Object} userBadgeData - User badge data
 * @returns {Promise<string>} Created user badge ID
 */
export async function awardBadge(userBadgeData) {
  try {
    // Prepare the user badge with timestamp first, then validate
    const userBadge = {
      ...userBadgeData,
      awardedAt: serverTimestamp(),
    };

    // Validate user badge data
    validateUserBadge({ ...userBadge, awardedAt: new Date() }); // Use actual date for validation

    // Check if user already has this badge
    const existingQuery = query(
      collection(db, COLLECTIONS.USER_BADGES),
      where("userId", "==", userBadgeData.userId),
      where("badgeId", "==", userBadgeData.badgeId)
    );

    const existingSnapshot = await getDocs(existingQuery);
    if (!existingSnapshot.empty) {
      throw new Error("User already has this badge");
    }

    // Verify badge exists and is active
    const badge = await getBadgeById(userBadgeData.badgeId);
    if (!badge || !badge.isActive) {
      throw new Error("Badge not found or inactive");
    }

    const docRef = await addDoc(
      collection(db, COLLECTIONS.USER_BADGES),
      userBadge
    );

    console.log("Badge awarded successfully:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("Error awarding badge:", error);
    // Preserve specific business logic errors
    if (
      error.message === "User already has this badge" ||
      error.message === "Badge not found or inactive"
    ) {
      throw error;
    }
    throw new Error("Failed to award badge");
  }
}

/**
 * Remove a badge from a user
 * @param {string} userId - User ID
 * @param {string} badgeId - Badge ID
 * @returns {Promise<void>}
 */
export async function removeBadge(userId, badgeId) {
  try {
    const userBadgeQuery = query(
      collection(db, COLLECTIONS.USER_BADGES),
      where("userId", "==", userId),
      where("badgeId", "==", badgeId)
    );

    const snapshot = await getDocs(userBadgeQuery);

    if (snapshot.empty) {
      throw new Error("User badge not found");
    }

    // Remove all instances (should only be one, but just in case)
    const batch = writeBatch(db);
    snapshot.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();

    console.log("Badge removed successfully");
  } catch (error) {
    console.error("Error removing badge:", error);
    // Preserve specific business logic errors
    if (error.message === "User badge not found") {
      throw error;
    }
    throw new Error("Failed to remove badge");
  }
}

/**
 * Award multiple badges to a user (from review submission)
 * @param {string} userId - User ID
 * @param {Array} badgeIds - Array of badge IDs
 * @param {string} reviewId - Review ID that triggered the badges
 * @returns {Promise<Array>} Array of created user badge IDs
 */
export async function awardBadgesFromReview(userId, badgeIds, reviewId) {
  try {
    if (!Array.isArray(badgeIds) || badgeIds.length === 0) {
      return [];
    }

    const awardedBadgeIds = [];

    for (const badgeId of badgeIds) {
      try {
        const userBadgeId = await awardBadge({
          userId,
          badgeId,
          awardedBy: BADGE_AWARD_SOURCES.REVIEW,
          reviewId,
          metadata: {
            source: "review_submission",
            reviewId,
          },
        });
        awardedBadgeIds.push(userBadgeId);
      } catch (error) {
        // Log error but continue with other badges
        console.warn(
          `Failed to award badge ${badgeId} to user ${userId}:`,
          error.message
        );
      }
    }

    console.log(
      `Awarded ${awardedBadgeIds.length} badges to user ${userId} from review ${reviewId}`
    );
    return awardedBadgeIds;
  } catch (error) {
    console.error("Error awarding badges from review:", error);
    throw new Error("Failed to award badges from review");
  }
}

/**
 * Get badge statistics for admin dashboard
 * @returns {Promise<Object>} Badge statistics
 */
export async function getBadgeStatistics() {
  try {
    const [badgesSnapshot, userBadgesSnapshot] = await Promise.all([
      getDocs(collection(db, COLLECTIONS.BADGES)),
      getDocs(collection(db, COLLECTIONS.USER_BADGES)),
    ]);

    const totalBadges = badgesSnapshot.size;
    const activeBadges = badgesSnapshot.docs.filter(
      (doc) => doc.data().isActive
    ).length;
    const totalAwarded = userBadgesSnapshot.size;

    // Badge distribution by category
    const categoryDistribution = {};
    badgesSnapshot.forEach((doc) => {
      const badge = doc.data();
      if (badge.isActive) {
        categoryDistribution[badge.category] =
          (categoryDistribution[badge.category] || 0) + 1;
      }
    });

    // Most popular badges
    const badgeAwardCounts = {};
    userBadgesSnapshot.forEach((doc) => {
      const userBadge = doc.data();
      badgeAwardCounts[userBadge.badgeId] =
        (badgeAwardCounts[userBadge.badgeId] || 0) + 1;
    });

    const mostPopularBadges = Object.entries(badgeAwardCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([badgeId, count]) => ({ badgeId, count }));

    return {
      totalBadges,
      activeBadges,
      totalAwarded,
      categoryDistribution,
      mostPopularBadges,
    };
  } catch (error) {
    console.error("Error fetching badge statistics:", error);
    throw new Error("Failed to fetch badge statistics");
  }
}

/**
 * Initialize default badges (run once during setup)
 * @returns {Promise<Array>} Array of created badge IDs
 */
export async function initializeDefaultBadges() {
  try {
    const defaultBadges = [
      // Personality badges
      {
        name: "Friendly",
        description: "Known for being warm and welcoming to students",
        category: BADGE_CATEGORIES.PERSONALITY,
        icon: "😊",
        color: "green",
        isActive: true,
        sortOrder: 1,
      },
      {
        name: "Patient",
        description: "Takes time to help students understand concepts",
        category: BADGE_CATEGORIES.PERSONALITY,
        icon: "🧘",
        color: "blue",
        isActive: true,
        sortOrder: 2,
      },
      {
        name: "Encouraging",
        description:
          "Motivates and supports students in their learning journey",
        category: BADGE_CATEGORIES.PERSONALITY,
        icon: "💪",
        color: "yellow",
        isActive: true,
        sortOrder: 3,
      },
      // Teaching Style badges
      {
        name: "Good for Beginners",
        description:
          "Excellent at teaching fundamental concepts to new students",
        category: BADGE_CATEGORIES.TEACHING_STYLE,
        icon: "🌱",
        color: "green",
        isActive: true,
        sortOrder: 4,
      },
      {
        name: "Challenging",
        description: "Pushes students to reach their full potential",
        category: BADGE_CATEGORIES.TEACHING_STYLE,
        icon: "🏔️",
        color: "red",
        isActive: true,
        sortOrder: 5,
      },
      {
        name: "Makes Learning Fun",
        description: "Creates an enjoyable and engaging learning experience",
        category: BADGE_CATEGORIES.TEACHING_STYLE,
        icon: "🎉",
        color: "purple",
        isActive: true,
        sortOrder: 6,
      },
      // Expertise badges
      {
        name: "Very Knowledgeable",
        description: "Demonstrates deep expertise in their subject area",
        category: BADGE_CATEGORIES.EXPERTISE,
        icon: "🧠",
        color: "indigo",
        isActive: true,
        sortOrder: 7,
      },
      {
        name: "Flexible Schedule",
        description: "Accommodating with lesson times and scheduling",
        category: BADGE_CATEGORIES.EXPERTISE,
        icon: "⏰",
        color: "blue",
        isActive: true,
        sortOrder: 8,
      },
    ];

    const createdBadgeIds = [];

    for (const badgeData of defaultBadges) {
      try {
        const badgeId = await createBadge(badgeData);
        createdBadgeIds.push(badgeId);
      } catch (error) {
        console.warn(
          `Failed to create badge "${badgeData.name}":`,
          error.message
        );
      }
    }

    console.log(`Initialized ${createdBadgeIds.length} default badges`);
    return createdBadgeIds;
  } catch (error) {
    console.error("Error initializing default badges:", error);
    throw new Error("Failed to initialize default badges");
  }
}

export default {
  getBadges,
  getBadgeById,
  createBadge,
  updateBadge,
  deleteBadge,
  getUserBadges,
  awardBadge,
  removeBadge,
  awardBadgesFromReview,
  getBadgeStatistics,
  initializeDefaultBadges,
};
